import {
  type CartTransferResponse,
  type TransferProductsResponse,
  type AddToCartBffResponse,
  type Cart,
  type CartGraphQLInput,
  type CartResponse,
} from '@/features/shop/services/Cart/types'
import { normalizeCartResponse } from '@/features/shop/services/Cart/utils'
import { normalizeGraphQLMutationCart } from '@/features/shop/services/Cart/utils/normalizeGraphQLCart'
import { apiGet, apiPost, apiPut } from '@/features/shared/utils/dataFetching'
import { fetchGraphql } from '@/features/shared/utils/dataFetching/reactQuery/graphql'
import { logError } from '@/features/shared/utils/logger'
import { CartUpdateProductDocument, type ProductDataInput } from '@/gql/graphql'
import { type StoreParams } from '@/features/shared/utils/dataFetching/storeParams'
import { getAnonymousId } from '@shipt/analytics-member-web'
import { type QueryFunctionContext } from '@tanstack/react-query'
import { type Address } from '@/features/account/services/Addresses/types'
import { type CartQueryKey } from '@/features/shop/services/Cart/queries'
import { productBrokerTimeoutMessage } from '@/features/shop/services/Cart/constants'
import { type HierarchicalCategory } from '@/features/shop/services/Product/types'
import { type GlobalStoreParams } from '@shared/types'

export const fetchCart = async (context: QueryFunctionContext<CartQueryKey>): Promise<Cart> => {
  const { signal, queryKey } = context
  const [, storeParams, state] = queryKey
  const cartResponse = await apiGet<CartResponse>({
    config: {
      url: 'cart/v2/shopping_cart.json',
      params: { state, include_promos: true },
      signal,
    },
    options: {
      storeParams,
      ignoredMessages: [productBrokerTimeoutMessage],
    },
    context,
    fetcherName: 'fetchCart',
  })
  return normalizeCartResponse(cartResponse)
}

export const updateCart = async (
  { customerId, retailerData }: Pick<CartGraphQLInput, 'customerId' | 'retailerData'>,
  productData: ProductDataInput,
) => {
  const response = await fetchGraphql(
    CartUpdateProductDocument,
    {
      input: {
        bypassData: {
          includeProductData: false,
          includePromotionData: true,
          includeFeeData: false,
          includeComplianceData: false,
        },
        customerId,
        retailerData,
        productsData: [productData],
      },
    },
    { authOptional: true },
  )
  if (!response.cartUpdateProduct.cart) {
    const error = new Error('GraphQL cart update failed')
    logError(error)
    throw error
  }
  return normalizeGraphQLMutationCart(response.cartUpdateProduct.cart)
}

export const convertCart = (
  params: Omit<StoreParams, 'zip'>,
  address: Address,
  user_id: number,
) => {
  const anonymous_id = getAnonymousId()
  const { metro_id, store_id, store_location_id } = params
  const { street1: street_1, street2: street_2, city, state, zip_code: zip } = address
  const apiData = {
    anonymous_id,
    user_id,
    metro_id,
    store_id,
    store_location_id,
    street_1,
    street_2,
    city,
    state,
    zip,
  }
  return apiPut({
    config: { url: '/api/v1/member_conversion', data: apiData },
    fetcherName: 'convertCart',
  })
}

type AddToCartProduct = {
  id: number
  quantity: number
  unitWeight?: number
  note?: string
  name: string
  type: string
  couponId?: string
  isCustomProduct?: boolean
  hasCustomLabel?: boolean
  hierarchical_categories?: HierarchicalCategory
  rootCategoryId?: number
}

export const addProductsToCart = async ({
  state,
  storeParams,
  products,
  fetcherName,
}: {
  state: string
  storeParams: StoreParams
  products: AddToCartProduct[]
  fetcherName: string
}) =>
  apiPost<AddToCartBffResponse>({
    config: {
      url: 'member-web-bff/v1/cart/add-to-cart',
      data: {
        state,
        storeParams,
        products: products.map((product) => ({
          id: product.id,
          quantity: product.quantity,
          unitWeight: product.unitWeight,
          note: product.note,
          name: product.name,
          type: product.type,
          couponId: product.couponId,
          isCustomProduct: product.isCustomProduct,
          hasCustomLabel: product.hasCustomLabel,
          hierarchical_categories: product.hierarchical_categories,
          // TODO add this to BFF -- https://app.clickup.com/t/86b6qvnhj
          rootCategoryId: product.rootCategoryId,
        })),
      },
    },
    options: {
      ignoredMessages: [productBrokerTimeoutMessage, 'Cannot add more than 500 items'],
    },
    fetcherName,
    tag: 'bff',
  })

type MergeCartResponse = Omit<CartResponse, 'shopping_cart_lines_oos'> & {
  shopping_cart_lines_oos: CartResponse['shopping_cart_lines_oos'] | null
  fees: CartResponse['fees'] | null
  promotions: CartResponse['promotions'] | null
}
export const mergeCart = async ({
  store_id,
  metro_id,
  anonymous_id,
  store_location_id,
}: {
  store_id: number
  metro_id: number
  anonymous_id: string
  store_location_id: number
}) => {
  return apiPost<MergeCartResponse>({
    config: {
      url: 'cart/v3/carts/merge',
      data: {
        anonymous_id,
        store: {
          store_id,
          metro_id,
          store_location_id,
        },
      },
    },
    fetcherName: 'mergeCart',
  })
}

/**
 * assistantParams are encoded search params representing the source cart
 * the store_id, metro_id, and store_location_id are for the destination the cart will be transferred to
 */
export const transferCart = async ({
  store_id,
  metro_id,
  assistantParams,
  store_location_id,
}: {
  store_id: number
  metro_id: number
  store_location_id: number
  assistantParams: string
}) => {
  return apiPost<CartTransferResponse>({
    config: {
      // Query params are source cart params
      url: `cart/v2/shopping_cart/transfer_available_products?${assistantParams}`,
      // body is destination cart params (where we are transferring the cart to)
      data: {
        metro_id,
        store_id,
        store_location_id,
      },
    },
    fetcherName: 'transferCart',
  })
}

/**
 * Transfers products from one retailer cart to another.
 * Query params represent the source cart, body represents the destination cart.
 */
export const transferProducts = async ({
  source,
  destination,
}: {
  source: GlobalStoreParams
  destination: GlobalStoreParams
}) => {
  const sourceParams = new URLSearchParams({
    store_id: String(source.store_id),
    metro_id: String(source.metro_id),
    store_location_id: String(source.store_location_id),
  })

  return apiPost<TransferProductsResponse>({
    config: {
      url: `cart/v3/carts/transfer_products?${sourceParams}`,
      data: {
        store: {
          store_id: destination.store_id,
          metro_id: destination.metro_id,
          store_location_id: destination.store_location_id,
        },
      },
    },
    fetcherName: 'transferProducts',
  })
}
